import React, { useMemo } from 'react';

import { message } from 'antd';
import moment from 'moment';
import classnames from 'classnames';
import { SOURCE } from '@laigu/constants';
import { useFunction } from '@laigu/store';

import Reffer from '../reffer';

import BrowserIcon from '../../browser-icon';

import { getInitVisitor } from '../selector';
import { List } from '../client-common';

import { VisitPage } from '../typings';

import style from './index.less';
import clientStyle from '../index.less';

import { copyText } from '@/utils/clipboard';

const { searchListSource }: { [key: string]: any } = SOURCE;

const browserIconStyle = { margin: '0 5px 0 10px' };

type KeyWords = {
  createdTime: string;
  searchEngine: string;
  keyword: string;
};

const Keywords = ({ keywords }: { keywords: KeyWords[] }): JSX.Element => {
  return (
    <>
      {keywords.map((val: KeyWords, ind: number) => {
        const { createdTime, searchEngine, keyword } = val;
        return (
          <div key={`${createdTime}${ind}`}>
            <span>{createdTime}</span>
            <span style={browserIconStyle}>
              <BrowserIcon browser={searchEngine} />
            </span>
            <span>{keyword}</span>
          </div>
        );
      })}
    </>
  );
};
const parseValue = (list: string[], splitString = ' '): string => list.filter(val => val).join(splitString);

type SourceInfoProps = {
  visitInfo: VisitInfo;
  isOnline?: boolean;
  visitorPage?: VisitPage[];
  customerAttrs: Record<string, string>;
  advertise?: Record<string, string>;
  source?: string;
  subSource?: string;
};

function filterArray<T, U>(array: Array<T>, mapFn: (x: T, i: number) => U | 'filter'): U[] {
  const result: U[] = [];
  for (let i = 0; i < array.length; i++) {
    const mapped = mapFn(array[i], i);
    if (mapped !== 'filter') {
      result.push(mapped);
    }
  }
  return result;
}

const SourceInfo = ({ visitInfo, visitorPage = [], customerAttrs, advertise = {}, source, subSource }: SourceInfoProps) => {
  const initedVisitInfo = getInitVisitor(visitInfo);
  const {
    app_name,
    app_version = '',
    utm = {
      utm_source: '',
      utm_medium: '',
      utm_term: '',
      utm_content: '',
      utm_campaign: ''
    }
  } = initedVisitInfo;
  const {
    advertiser_name = '',
    advertiser_id = '',
    campaign_name = '',
    campaign_id = '',
    creativity_name = '',
    creativity_id = '',
    wecom_link_id = '',
    wecom_link_name = '',
    incoming_note_id = '',
    incoming_note_title = '',
    comment_note_id = '',
    comment_note_title = '',
    promotion_id = ''
  } = customerAttrs;

  const visitFunc = useFunction('visit_source');
  const utmFunc = useFunction('utm_check');
  const pages = visitorPage;

  const app = app_name ? parseValue([app_name, app_version]) : '';

  const handleCopy = (content: string) => {
    copyText(content);
    message.success('已复制ID');
  };

  const sourceInfoData = visitFunc
    ? {
        label: '来源',
        children: (
          <div className={style['source-info']}>
            <Reffer
              visitInfo={initedVisitInfo}
              source={source}
              subSource={subSource}
              sourceType={customerAttrs.source_type}
            />
          </div>
        ),
        contentClass: style['content-box']
      }
    : null;

  const incomingNoteData = visitFunc
    ? {
        label: '来源笔记',
        children: <span onClick={() => handleCopy(incoming_note_id)}>{incoming_note_title}</span>
      }
    : null;

  // 评论笔记：顾客最近一次意向评论所在的小红书笔记，空值不展示该行
  const commentNoteData =
    visitFunc && comment_note_title
      ? {
          label: '评论笔记',
          children: <span onClick={() => handleCopy(comment_note_id)}>{comment_note_title}</span>
        }
      : null;

  const adInfoAData = visitFunc
    ? {
        label: '广告主',
        children: (
          <div className={style['source-info']}>
            {advertiser_id && (
              <span style={{ display: 'flex' }} onClick={() => handleCopy(advertiser_id)}>
                <span
                  style={{ display: 'inline-block', maxWidth: '170px', width: 'auto' }}
                  className={style['visiting-page']}
                >
                  {advertiser_name}
                </span>
                -{advertiser_id}
              </span>
            )}
          </div>
        ),
        contentClass: style['content-box']
      }
    : null;
  const planInfoAData = visitFunc
    ? {
        label: '计划',
        children: (
          <div className={style['source-info']}>
            {campaign_id && (
              <span style={{ display: 'flex' }} onClick={() => handleCopy(campaign_id)}>
                <span
                  style={{ display: 'inline-block', maxWidth: '170px', width: 'auto' }}
                  className={style['visiting-page']}
                >
                  {campaign_name}
                </span>
                -{campaign_id}
              </span>
            )}
          </div>
        ),
        contentClass: style['content-box']
      }
    : null;
  const creativityInfoAData = visitFunc
    ? {
        label: '创意',
        children: (
          <div className={style['source-info']}>
            {creativity_id && (
              <span style={{ display: 'flex' }} onClick={() => handleCopy(creativity_id)}>
                <span
                  style={{ display: 'inline-block', maxWidth: '170px', width: 'auto' }}
                  className={style['visiting-page']}
                >
                  {creativity_name}
                </span>
                -{creativity_id}
              </span>
            )}
          </div>
        ),
        contentClass: style['content-box']
      }
    : null;

  // 抖音渠道新增 广告ID
  const promotionInfoAData = visitFunc
    ? {
        label: '广告ID',
        children: (
          <div className={style['source-info']}>
            {promotion_id && (
              <span style={{ display: 'flex' }} onClick={() => handleCopy(promotion_id)}>
                {promotion_id}
              </span>
            )}
          </div>
        ),
        contentClass: style['content-box']
      }
    : null;

  const wecomData = visitFunc
    ? {
        label: '企业微信',
        children: (
          <div className={style['source-info']}>
            {wecom_link_id ? (
              <a
                target='_blank'
                href={`https://work.weixin.qq.com/ca/${wecom_link_id}`}
                style={{ display: 'inline-block', maxWidth: '170px', width: 'auto' }}
                className={style['visiting-page']}
              >
                {wecom_link_name}
              </a>
            ) : (
              <span
                style={{
                  display: 'inline-block',
                  maxWidth: '170px',
                  width: 'auto',
                  color: '#ccc'
                }}
                className={style['visiting-page']}
              >
                未添加企业微信
              </span>
            )}
          </div>
        ),
        contentClass: style['content-box']
      }
    : null;

  const applicationData = [{ label: '应用', children: app }].filter(val => !!val.children.trim());

  // const sdkData = sdk_name
  //   ? {
  //       label: 'SDK',
  //       children: parseValue([sdk_name, sdk_version])
  //     }
  //   : null;

  const keywords = useMemo(() => {
    if (!pages || pages.length === 0) {
      return [];
    }
    return pages
      .filter(page => page && page.source_keyword && searchListSource.indexOf(page.source) >= 0)
      .map(val => ({
        keyword: val.source_keyword,
        searchEngine: val.source,
        createdTime: moment(moment.utc(val.created_on).valueOf()).format('MM-DD HH:mm:ss')
      }))
      .slice(0, 3);
  }, [pages]);

  const keywordsData =
    keywords && keywords.length > 0
      ? {
          label: '最近关键词',
          children: <Keywords keywords={keywords} />
        }
      : null;

  const utmList: VisitUtmItemType[] = ['utm_source', 'utm_medium', 'utm_term', 'utm_content', 'utm_campaign'];
  const utmData =
    utmFunc && utm
      ? utmList.reduce((prev: Array<{ label: string; children: string }>, now: VisitUtmItemType) => {
          if (utm[now]) {
            prev.push({
              label: now,
              children: utm[now]
            });
          }
          return prev;
        }, [])
      : [];

  const advertise_account_id =
    advertise?.advertise_account_id != undefined && advertise?.advertise_account_id != null
      ? { label: '广告主ID', children: advertise?.advertise_account_id }
      : null;

  const advertise_group_id =
    advertise?.advertise_group_id != undefined && advertise?.advertise_group_id != null
      ? { label: '广告组id', children: advertise?.advertise_group_id }
      : null;

  const advertise_plan_id =
    advertise?.advertise_plan_id != undefined && advertise?.advertise_plan_id != null
      ? { label: '广告计划id', children: advertise?.advertise_plan_id }
      : null;

  const advertise_creative_id =
    advertise?.advertise_creative_id != undefined && advertise?.advertise_creative_id != null
      ? { label: '广告创意id', children: advertise?.advertise_creative_id }
      : null;

  const data = [
    sourceInfoData,
    incomingNoteData,
    commentNoteData,
    adInfoAData,
    planInfoAData,
    creativityInfoAData,
    promotionInfoAData,
    wecomData,
    ...applicationData,
    keywordsData,
    ...utmData,
    advertise_account_id,
    advertise_group_id,
    advertise_plan_id,
    advertise_creative_id
  ];

  const filterNull = filterArray(data, e => (e === null ? 'filter' : e));

  return (
    <div id='source' className={classnames(style.source, clientStyle.tabpane)}>
      <List data={filterNull} className={style['visiting-list']} />
    </div>
  );
};

export default SourceInfo;
